export type ConnectorBackend = "native" | "composio";

export type BackendRoutedConnector = {
  id: string;
  backend?: ConnectorBackend;
  connected_backends?: ConnectorBackend[];
};

export function selectedConnectorBackend(
  connector: BackendRoutedConnector,
): ConnectorBackend {
  return connector.backend ?? "native";
}

// Proxied backends (Composio) mount their routes under
// `/api/connectors/<backend>/<id>`; native connectors under `/api/connectors/<id>`.
function backendPrefix(backend: ConnectorBackend): string {
  return backend === "native" ? "" : `${backend}/`;
}

export function connectorOAuthStartPath(connector: BackendRoutedConnector): string {
  const id = encodeURIComponent(connector.id);
  return `/api/connectors/${backendPrefix(selectedConnectorBackend(connector))}${id}/oauth/start`;
}

export function connectorDisconnectSlug(connector: BackendRoutedConnector): string {
  const id = encodeURIComponent(connector.id);
  return `${backendPrefix(selectedConnectorBackend(connector))}${id}`;
}

/** Whether an OAuth return must call `/verify` to learn, and record, the result.
 *
 * Composio replaces the query's `status` with its own value (`success`, …) on
 * the way back (ENG-5566), so a Composio return always verifies; `/verify`
 * lists the caller's ACTIVE accounts and is the authoritative result. Native
 * OAuth reports its own final result and never verifies. */
export function oauthReturnNeedsVerify(
  status: string | null | undefined,
  backend: string | null | undefined,
): boolean {
  if (!status) return false;
  return backend === "composio";
}

/** Path the web app POSTs after a proxied backend redirects back (the
 * connection is only recorded by this verify call). */
export function connectorVerifyPath(connectorId: string, backend: ConnectorBackend): string {
  return `/api/connectors/${backendPrefix(backend)}${encodeURIComponent(connectorId)}/verify`;
}

export function connectorDisconnectSlugs(
  connector: BackendRoutedConnector,
): string[] {
  const backends = connector.connected_backends?.length
    ? Array.from(new Set(connector.connected_backends))
    : [selectedConnectorBackend(connector)];
  return backends.map((backend) =>
    connectorDisconnectSlug({ id: connector.id, backend }),
  );
}
